import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { CATEGORIES } from "@/components/reports/reportDefs";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { inr0 } from "@/lib/format";

const KEYS = ["date_from", "date_to", "customer_id", "status", "lm_type", "movement_type", "direction", "sales_owner", "asset_status", "warranty", "contract_type", "q"];

export function ReportView({ def, onRowClick }) {
  const [f, setF] = useState({});
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(f.q || ""), 350);
    return () => clearTimeout(t);
  }, [f.q]);
  const qs = KEYS.filter((k) => (k === "q" ? q : f[k])).map((k) => `${k}=${encodeURIComponent(k === "q" ? q : f[k])}`).join("&");
  const { data, loading, error } = useFetch(`/reports/${def.key}${qs ? `?${qs}` : ""}`);
  const total = def.sum && data ? data.reduce((s, r) => s + (r[def.sum] || 0), 0) : null;
  return (
    <div data-testid={`report-${def.key}`}>
      <ReportFilters def={def} f={f} setF={setF} />
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-600" data-testid="report-summary">
        <span>Rows: <b className="text-slate-900" data-testid="report-row-count">{loading ? "…" : data?.length ?? 0}</b></span>
        {total !== null && <span>Total {def.sum === "pending_basic" ? "pending" : def.sum === "open" ? "open" : "value"} (basic, without GST): <b className="font-mono text-slate-900" data-testid="report-total">{inr0(total)}</b></span>}
        {error && <span className="text-red-600">{error}</span>}
      </div>
      <DataTable testid={`report-table-${def.key}`} tableId={`report-${def.key}`} columns={def.cols} rows={data} rowId={(r) => r.id} onRowClick={onRowClick} exportName={def.title.replace(/[^A-Za-z0-9]+/g, "_")} exportPerm="reports.export" empty="No rows for these filters" />
    </div>
  );
}

const SRC = { "dispatch-register": "dispatch.view", "pending-dispatch": "dispatch.view", movements: "logistics.view", "quote-status": "sales.view", "quote-conversion": "sales.view", "assets-warranty": "assets.view", "plan-register": "planning.view", "qc-register": "qc.view", "delivered-pod": "dispatch.view", "logistics-budget": "logistics.view", "billing-due": "orders.view" };

export default function Reports() {
  const { cat = "orders" } = useParams();
  const nav = useNavigate();
  const { can } = useMe();
  const cats = CATEGORIES.map((x) => ({ ...x, reports: x.reports.filter((r) => can(SRC[r.key] || "orders.view")) })).filter((x) => x.reports.length);
  const [pick, setPick] = useState({});
  if (!cats.length) return <div data-testid="reports-page"><PageHeader title="Reports" subtitle="You don't have access to any section these reports draw on. Ask an admin." /></div>;
  const c = cats.find((x) => x.key === cat) || cats[0];
  const def = c.reports.find((r) => r.key === pick[c.key]) || c.reports[0];
  return (
    <div data-testid="reports-page">
      <PageHeader title={`Reports · ${c.title}`} subtitle="Operational list reports from live data. Money is basic value without GST. Use Export to download Excel / CSV." />
      <div className="flex flex-wrap gap-2 mb-2" data-testid="report-categories">
        {cats.map((x) => <button key={x.key} onClick={() => nav(`/reports/${x.key}`)} data-testid={`report-cat-${x.key}`}
          className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${x.key === c.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{x.title}</button>)}
      </div>
      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200" data-testid="report-tabs">
        {c.reports.map((r) => <button key={r.key} onClick={() => setPick({ ...pick, [c.key]: r.key })} data-testid={`report-tab-${r.key}`}
          className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${r.key === def.key ? "border-sky-700 text-sky-800 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{r.title}</button>)}
      </div>
      <ReportView key={def.key} def={def} />
    </div>
  );
}
